import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format, parseISO } from 'date-fns';
import { cardAction } from '@shared/api/services';
import { useProfile } from '@shared/api/useProfile';
import { useChatStore, MESSAGE_TYPE } from '@entities/messanger';
import { useRemoveAppointmentModal } from '@features/appointment/remove-appointment';
import { useOrderModal } from '@features/student/order';
import { useTeacherTimezone } from '../api/use-teacher-timezone';
import { convertSlotDateTime } from '../lib/convert-slot-time';

const BOOKING_ACTION_KEY = 'book';
const CANCEL_ACTION_KEY = 'cancel';
const PAY_ACTION_KEY = 'pay';

const PACKAGE_MESSAGE_TYPES = [
  MESSAGE_TYPE.PACKAGE_CARD,
  MESSAGE_TYPE.PACKAGE_OFFER_CARD,
];

// Пакетная ссылка в url действия (на случай нестандартного ключа)
const isPackageUrl = (url) => /\/packages?|package-offers?|\/packs?/.test(url.pathname + url.search);

// Бэк отдаёт дату в ISO (2026-09-23), страница бронирования ждёт dd.MM.yyyy
const toBookingDate = (raw) => {
  try {
    const parsed = parseISO(raw);
    if (Number.isNaN(parsed.getTime())) return raw;
    return format(parsed, 'dd.MM.yyyy');
  } catch {
    return raw;
  }
};

// Обработка действий карточки чата (message.card.actions).
// Бронирование — переход на страницу бронирования с выбранными параметрами,
// отмена — модалка отмены занятия, прочие действия — самодостаточный
// HTTP-запрос (method + url).
export const useCardAction = (chatId) => {
  const navigate = useNavigate();
  const [pendingKey, setPendingKey] = useState(null);
  const { data: profile } = useProfile();
  const teacherSlug = useChatStore(
    (state) =>
      state.chats.find((c) => c.id === chatId)?.participants?.[0]?.teacher_slug
  );
  const { data: teacherTimezone } = useTeacherTimezone(teacherSlug);
  const { open: openRemoveAppointment } = useRemoveAppointmentModal();
  const { open: openOrderModal } = useOrderModal();

  const run = useCallback(
    async (action, message) => {
      const key = `${message?.id}:${action.key}`;
      setPendingKey(key);
      try {
        const url = new URL(action.url, window.location.origin);

        // Кнопки покупки пакета (предложение или активный пакет) ведут на
        // страницу пакетов преподавателя — независимо от ключа действия
        if (
          action.key !== CANCEL_ACTION_KEY &&
          (PACKAGE_MESSAGE_TYPES.includes(message?.type) || isPackageUrl(url))
        ) {
          if (!teacherSlug) {
            toast.error('Не удалось определить преподавателя');
            return false;
          }
          navigate(`/teacher/packs/${teacherSlug}`);
          return true;
        }

        switch (action.key) {
          case BOOKING_ACTION_KEY: {
            if (!teacherSlug) {
              toast.error('Не удалось определить преподавателя');
              return false;
            }

            const payload = message?.payload || {};
            // Сырые date/time в payload — в таймзоне преподавателя.
            // Конвертируем в таймзону ученика, чтобы параметры совпали
            // со слотами availability API (они отдаются в таймзоне профиля).
            const converted = convertSlotDateTime(
              payload.date || url.searchParams.get('date'),
              payload.time || url.searchParams.get('time'),
              teacherTimezone,
              profile?.timezone
            );

            const params = new URLSearchParams();
            if (converted) {
              params.set('date', converted.date);
              params.set('time', converted.time);
            } else {
              const date = url.searchParams.get('date');
              if (date) params.set('date', toBookingDate(date));
              const time = url.searchParams.get('time');
              if (time) params.set('time', time);
            }
            const instrumentId =
              url.searchParams.get('instrument_id') ?? payload.instrument_id;
            if (instrumentId) params.set('instrument', instrumentId);
            const query = params.toString();
            navigate(`/teacher/booking/${teacherSlug}${query ? `?${query}` : ''}`);
            return true;
          }

          case CANCEL_ACTION_KEY: {
            // url вида /appointments/1008/cancel
            const match = url.pathname.match(/\/appointments\/(\d+)/);
            if (!match) {
              toast.error('Не удалось определить занятие');
              return false;
            }
            openRemoveAppointment({ appointmentId: Number(match[1]) });
            return true;
          }

          case PAY_ACTION_KEY: {
            // Оплата — модалка заказа. id заказа: из payload карточки
            // (entity_type: 'order') или из url действия /orders/123/...
            const payloadId = message?.payload?.entity_id;
            const match = url.pathname.match(/\/orders?\/(\d+)/);
            const orderId = payloadId ?? (match ? Number(match[1]) : null);
            if (!orderId) {
              toast.error('Не удалось определить заказ');
              return false;
            }
            openOrderModal({ orderId });
            return true;
          }

          default: {
            await cardAction(action.method || 'POST', action.url);
            toast.success('Готово');
            return true;
          }
        }
      } catch (e) {
        return false;
      } finally {
        setPendingKey(null);
      }
    },
    [teacherSlug, teacherTimezone, profile?.timezone, navigate, openRemoveAppointment, openOrderModal]
  );

  return { run, pendingKey };
};
